import {EPermission, EResource} from '../../../types/permission.type'

/** Child route path of a section, relative to `…/settings`. */
export type TSettingsPath =
  | 'preferences'
  | 'users'
  | 'dictionary'
  | 'referential'
  | 'junctions'
  | 'sectors'
  | 'subsectors'
  | 'geomarkers'
  | 'trainstations'
  | 'switches'
  | 'access'
  | 'protections'
  | 'protection-types'
  | 'teams'
  | 'resources'
  | 'resource-roles'

/**
 * Navigation entries of the settings screen.
 */
export type TSettingsEntry = {
  labelKey: string
  path: TSettingsPath
  /** Permission gating both the nav entry and its route. */
  resource: EResource
  permission: EPermission
  /**
   * Client-wide configuration, shared by every project of the client: no
   * geographic preview, and a discreet scope note above the screen.
   */
  clientScoped?: boolean
}

export type TSettingsGroup = {
  titleKey: string
  entries: TSettingsEntry[]
}

type TCan = (permission: EPermission, resource: EResource) => boolean

/**
 * Grouped by task, not by scope: users look for "protections" or "teams", not
 * for "client" vs "project" settings. Scope only shows as a note on the screen.
 */
export const SETTINGS_GROUPS: TSettingsGroup[] = [
  {
    titleKey: 'settings:groupGeneral',
    entries: [
      {
        path: 'preferences',
        labelKey: 'settings:preferences',
        resource: EResource.NETWORK,
        permission: EPermission.CAN_UPDATE,
      },
      {
        path: 'users',
        labelKey: 'dashboard:user',
        resource: EResource.USER,
        permission: EPermission.CAN_VIEW,
        clientScoped: true,
      },
      {
        path: 'dictionary',
        labelKey: 'dashboard:dictionary',
        resource: EResource.DICTIONARY,
        permission: EPermission.CAN_VIEW,
        clientScoped: true,
      },
    ],
  },
  {
    titleKey: 'settings:groupNetwork',
    entries: [
      {
        path: 'referential',
        labelKey: 'referential:referential',
        resource: EResource.TRACK,
        permission: EPermission.CAN_VIEW,
      },
      {
        path: 'junctions',
        labelKey: 'dashboard:junction',
        resource: EResource.JUNCTION,
        permission: EPermission.CAN_VIEW,
      },
      {
        path: 'sectors',
        labelKey: 'sector:sectors',
        resource: EResource.SECTOR,
        permission: EPermission.CAN_VIEW,
      },
      {
        path: 'subsectors',
        labelKey: 'tooltip:subSector',
        resource: EResource.SUB_SECTOR,
        permission: EPermission.CAN_VIEW,
      },
      {
        path: 'geomarkers',
        labelKey: 'tooltip:geoMarker',
        resource: EResource.GEO_MARKER,
        permission: EPermission.CAN_VIEW,
      },
      {
        path: 'trainstations',
        labelKey: 'tooltip:station',
        resource: EResource.TRAIN_STATION,
        permission: EPermission.CAN_VIEW,
      },
      {
        path: 'switches',
        labelKey: 'tooltip:switch',
        resource: EResource.SWITCH,
        permission: EPermission.CAN_VIEW,
      },
      {
        path: 'access',
        labelKey: 'tooltip:access',
        resource: EResource.POINT_ACCESS,
        permission: EPermission.CAN_VIEW,
      },
    ],
  },
  {
    titleKey: 'settings:groupProtections',
    entries: [
      {
        path: 'protections',
        labelKey: 'protectionZone:protectionZones',
        resource: EResource.PROTECTION_ZONE,
        permission: EPermission.CAN_VIEW,
      },
      {
        path: 'protection-types',
        labelKey: 'dashboard:protectionTypes',
        resource: EResource.PROTECTION_TYPE,
        permission: EPermission.CAN_VIEW,
        clientScoped: true,
      },
    ],
  },
  {
    titleKey: 'settings:groupTeams',
    entries: [
      {
        path: 'teams',
        labelKey: 'dashboard:team',
        resource: EResource.TEAM,
        permission: EPermission.CAN_VIEW,
        clientScoped: true,
      },
      {
        path: 'resources',
        labelKey: 'resource:list',
        resource: EResource.RESOURCE,
        permission: EPermission.CAN_VIEW,
        clientScoped: true,
      },
      {
        path: 'resource-roles',
        labelKey: 'resource:roleList',
        resource: EResource.RESOURCE_ROLE,
        permission: EPermission.CAN_VIEW,
        clientScoped: true,
      },
    ],
  },
]

export const SETTINGS_ENTRIES: TSettingsEntry[] = SETTINGS_GROUPS.flatMap(
  group => group.entries,
)

/**
 * The groups restricted to the entries the user may open; empty groups are
 * dropped so no title shows without links under it.
 */
export const getVisibleSettingsGroups = (can: TCan): TSettingsGroup[] =>
  SETTINGS_GROUPS.map(group => ({
    ...group,
    entries: group.entries.filter(entry =>
      can(entry.permission, entry.resource),
    ),
  })).filter(group => group.entries.length > 0)

/**
 * Landing section of the settings screen: the first entry the user may open,
 * or null when none is allowed (the settings tab is then hidden).
 */
export const getDefaultSettingsSection = (can: TCan): string | null =>
  getVisibleSettingsGroups(can)[0]?.entries[0]?.path ?? null

/**
 * The settings entry shown at `pathname`: the segment right after `settings`.
 */
export const findSettingsEntry = (
  pathname: string,
): TSettingsEntry | undefined => {
  const segments = pathname.split('/').filter(Boolean)
  const settingsIndex = segments.indexOf('settings')
  if (settingsIndex < 0) return undefined
  const sectionPath = segments[settingsIndex + 1]
  return SETTINGS_ENTRIES.find(entry => entry.path === sectionPath)
}
